import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEnvironmentApiUrl } from "../services/General";
import useHttp from "./useHttp";
import { PatientInquiryAnswer, PollQuestion } from "../interfaces/Poll";

export const useSimulateInquiryAlerts = (answers: any[], questions: PollQuestion[]) => {
  const { t } = useTranslation();
  const { sendRequest: postAnswers } = useHttp();

  const [responseMessages, setResponseMessages] = useState<any[]>([]);

  const simulateInquiryAlertsObject: PatientInquiryAnswer[] = useMemo(() => {
    const answeredList = answers.filter(a => a.answered);

    return answeredList.map(a => {
      const question = questions.find(q => q.id === a.question_id);
      const selectedAnswer = question?.answers.find(ans => ans.id === a.answer_id);

      return {
        answer: selectedAnswer || undefined,
        inquiry_answer_id: selectedAnswer?.id || null,
        free_description: selectedAnswer?.description || "",
        carer_id: null,
        date: new Date().toISOString(),
      } as PatientInquiryAnswer;
    });
  }, [answers, questions]);

  useEffect(() => {
    if (simulateInquiryAlertsObject.length === 0) return;

    postAnswers(
      {
        url: getEnvironmentApiUrl() + "inquiry/simulate_inquiry_alerts",
        method: "POST",
        body: simulateInquiryAlertsObject,
      },
      (response: any) => {
        console.log("✅ Inquiry alerts simulated successfully");
        setResponseMessages(response);
      }
    );
  }, [simulateInquiryAlertsObject, postAnswers]);

  const alertsMessages = useMemo(() => {
    if (responseMessages.length === 0) return [];

    return responseMessages.map((response: any) => {
      const alert = response.inquiry_alert;
      if (!alert) return null;

      const question = questions.find(q => q.id === alert.inquiry_question_id);
      const answer = answers.find(a => a.question_id === alert.inquiry_question_id);

      const questionText = question?.description ?? t("global_value");
      const description = answer?.description || alert.value;
      const comparison = alert.comparison ?? "";

      return `${questionText} ${comparison} ${description}`;
    }).filter(Boolean) as string[];
  }, [responseMessages, questions, answers, t]);

  return { alertsMessages, responseMessages };
};